import { OTP_CODE_LENGTH, OTP_NORMALIZATION_MAP } from '@orn/global/auth/otpConstants.js';
import * as React from 'react';

import { cx } from '../../utils/cx';

import HintText from './HintText';

const normalizeOtpChar = (ch: string): string => OTP_NORMALIZATION_MAP[ch] ?? ch;

interface IOTPInputProps {
  /** Number of input fields. @default OTP_CODE_LENGTH (6) */
  length?: number;
  /** Current OTP value */
  value: string;
  /** Called when value changes */
  onChange: (value: string) => void;
  /** Called when all fields are filled */
  onComplete?: (code: string) => void;
  /** Whether the input is in an error state */
  isInvalid?: boolean;
  /** Error message to display below the input */
  errorMessage?: string;
  /** Whether the input is disabled */
  isDisabled?: boolean;
  /** Whether to auto-focus the first input on mount */
  autoFocus?: boolean;
}

const OTPInput = ({
  length = OTP_CODE_LENGTH,
  value,
  onChange,
  onComplete,
  isInvalid,
  errorMessage,
  isDisabled,
  autoFocus,
}: IOTPInputProps) => {
  const inputRefs = React.useRef<(HTMLInputElement | null)[]>([]);
  const chars = value.split('').concat(Array(length).fill('')).slice(0, length);

  // Auto-focus first input on mount
  React.useEffect(() => {
    if (autoFocus) {
      inputRefs.current[0]?.focus();
    }
  }, [autoFocus]);

  const focusInput = (index: number) => {
    inputRefs.current[index]?.focus();
  };

  const handlePointerDown = (index: number, e: React.PointerEvent<HTMLInputElement>) => {
    const firstEmptyIndex = chars.findIndex((c) => c === '');
    if (firstEmptyIndex !== -1 && index > firstEmptyIndex) {
      e.preventDefault();
      focusInput(firstEmptyIndex);
    }
  };

  const updateValue = React.useCallback(
    (newChars: string[]) => {
      const newValue = newChars.join('');
      onChange(newValue);
      if (newValue.length === length && onComplete) {
        onComplete(newValue);
      }
    },
    [onChange, onComplete, length]
  );

  const handleInput = React.useCallback(
    (index: number, inputValue: string) => {
      // Take only the last character typed (handles overtype)
      const char = normalizeOtpChar(inputValue.slice(-1).toUpperCase());
      if (!char) {
        return;
      }

      const newChars = [...chars];
      newChars[index] = char;
      updateValue(newChars);

      // Auto-advance to next field
      if (index < length - 1) {
        focusInput(index + 1);
      }
    },
    [chars, length, updateValue]
  );

  const handleKeyDown = React.useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace') {
        e.preventDefault();
        const newChars = [...chars];
        if (chars[index]) {
          // Clear current field
          newChars[index] = '';
          updateValue(newChars);
        } else if (index > 0) {
          // Move to previous field and clear it
          newChars[index - 1] = '';
          updateValue(newChars);
          focusInput(index - 1);
        }
      } else if (e.key === 'ArrowLeft' && index > 0) {
        e.preventDefault();
        focusInput(index - 1);
      } else if (e.key === 'ArrowRight' && index < length - 1) {
        e.preventDefault();
        focusInput(index + 1);
      }
    },
    [chars, length, updateValue]
  );

  const handlePaste = React.useCallback(
    (e: React.ClipboardEvent) => {
      e.preventDefault();
      const pasted = e.clipboardData
        .getData('text')
        .replace(/[\s-]/g, '')
        .toUpperCase()
        .split('')
        .map(normalizeOtpChar)
        .join('')
        .slice(0, length);

      if (pasted.length === 0) {
        return;
      }

      const newChars = [...chars];
      for (let i = 0; i < pasted.length; i++) {
        newChars[i] = pasted[i];
      }
      updateValue(newChars);

      // Focus the field after the last pasted character, or the last field
      const focusIndex = Math.min(pasted.length, length - 1);
      focusInput(focusIndex);
    },
    [chars, length, updateValue]
  );

  return (
    <div className="flex w-fit flex-col items-center gap-1.5">
      <div className="flex gap-1.5 sm:gap-2">
        {Array.from({ length }, (_, index) => (
          <input
            key={index}
            ref={(el) => {
              inputRefs.current[index] = el;
            }}
            type="text"
            inputMode="text"
            maxLength={2}
            autoComplete="one-time-code"
            placeholder="0"
            disabled={isDisabled}
            value={chars[index]}
            onChange={(e) => handleInput(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            onPointerDown={(e) => handlePointerDown(index, e)}
            aria-label={`Code digit ${index + 1}`}
            className={cx(
              'bg-primary text-primary text-display-sm sm:text-display-md outline-hidden h-14 w-12 rounded-lg text-center font-medium caret-transparent transition-colors duration-100 ease-linear sm:h-16 sm:w-16',
              'shadow-xs border-primary focus:border-brand border focus:border-2',
              'placeholder:text-placeholder_subtle',
              isInvalid && 'border-error_subtle focus:border-error focus:border-2',
              isDisabled && 'bg-disabled_subtle border-disabled text-disabled cursor-not-allowed'
            )}
          />
        ))}
      </div>
      {errorMessage && <HintText isInvalid>{errorMessage}</HintText>}
    </div>
  );
};

OTPInput.displayName = 'OTPInput';

export default OTPInput;
